import React, { useMemo } from "react";
import { Select, Spin } from "antd";
import { useMsal } from "@azure/msal-react";
import { useDebouncedCallback } from "use-debounce";
import { useSearchAD } from "hooks/requests/useSearchAD";

const { Option } = Select;

/** Stable empty array reference to avoid needless re-renders/memo invalidation. */
const EMPTY_VALUES: string[] = [];

export interface IADSearchSelectProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    /** "user" searches AD users (value = email); "group" searches AD groups (value = id). */
    mode?: "user" | "group";
    /** Values to hide from the options (e.g. to prevent duplicate selection). */
    excludeValues?: string[];
}

/**
 * Reusable Azure AD search dropdown for either users or groups.
 *
 * Each instance owns its search state, so multiple selects on the same screen
 * never interfere with one another.
 *
 * When rendered inside a <Form.Item name="...">, antd injects `value`/`onChange`
 * automatically — we simply forward them to the underlying Select.
 */
const ADSearchSelect: React.FC<IADSearchSelectProps> = ({
    value,
    onChange,
    placeholder,
    mode = "user",
    excludeValues = EMPTY_VALUES,
}) => {
    const msalInstance = useMsal();
    const { results, loadingSet, handleSearch } = useSearchAD({
        msalInstance,
        api: mode === "group" ? "/azure-ad/groups" : "/azure-ad/users",
    });

    const debouncedSearch = useDebouncedCallback(
        (term: string) => handleSearch(term, { id: "ad-search" }),
        300,
    );

    const loading = loadingSet.size > 0;

    const getValue = (item: { id: string; mail?: string }) =>
        mode === "group" ? item.id : item.mail ?? item.id;

    const getLabel = (item: { id: string; name: string; mail?: string }) =>
        mode === "group" ? item.name : `${item.name} (${item.mail ?? item.id})`;

    const options = useMemo(
        () =>
            // Inline the value resolution (rather than calling getValue) so this
            // memo's dependency list stays complete without an eslint-disable.
            results.filter((item: { id: string; mail?: string }) => {
                const itemValue = mode === "group" ? item.id : item.mail ?? item.id;
                return !excludeValues.includes(itemValue);
            }),
        [results, excludeValues, mode],
    );

    return (
        <Select
            showSearch
            filterOption={false}
            placeholder={placeholder}
            loading={loading}
            notFoundContent={loading ? <Spin size="small" /> : null}
            value={value}
            onChange={onChange}
            onSearch={(term) => debouncedSearch(term)}
            style={{ width: "100%" }}
        >
            {options.map((item) => (
                <Option key={item.id} value={getValue(item)}>
                    {getLabel(item)}
                </Option>
            ))}
        </Select>
    );
};

export { EMPTY_VALUES };
export default ADSearchSelect;
